import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import agent from "../../../../../app/api/agent";

const CompoundsStats = ({ projectId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [compounds, design, synthesis, activity] = await Promise.all([
          agent.Compounds.listByProject(projectId),
          agent.Compounds.subTable.list("design", projectId),
          agent.Compounds.subTable.list("synthesis", projectId),
          agent.Compounds.subTable.list("activity", projectId),
        ]);
        if (cancelled) return;

        const total = (compounds || []).length;
        const designCount = (design || []).length;
        const synthesisArr = synthesis || [];
        const synthesisTotal = synthesisArr.length;
        const activitySet = new Set((activity || []).map(r => r.entityId).filter(Boolean));
        const activityCount = activitySet.size;

        // Synthesis breakdown by status
        const statusCounts = { pending: 0, in_progress: 0, success: 0, failed: 0 };
        synthesisArr.forEach(r => {
          const extra = r.extraData ? JSON.parse(r.extraData) : {};
          const s = extra.status || "pending";
          if (s in statusCounts) statusCounts[s]++;
        });

        setStats({ total, designCount, synthesisTotal, synthesisArr, activityCount, statusCounts });
      } catch (err) {
        console.error("Failed to load compounds stats:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return <div style={{ padding: "20px 0", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" }}>Loading stats...</div>;
  }
  if (!stats) {
    return <div style={{ padding: "20px 0", textAlign: "center", color: "#ef4444", fontSize: "0.85rem" }}>Failed to load stats</div>;
  }

  const { total, designCount, synthesisTotal, activityCount, statusCounts } = stats;
  const synPct = (v) => synthesisTotal > 0 ? ((v / synthesisTotal) * 100).toFixed(0) : "0";

  const cardStyle = {
    flex: "1 1 0",
    minWidth: 0,
    textAlign: "center",
    padding: "14px 8px",
    borderRadius: 10,
    background: "#fff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  };
  const numberStyle = { fontSize: "1.6rem", fontWeight: 700, display: "block", lineHeight: 1.2 };
  const labelStyle = { fontSize: "0.75rem", color: "#64748b", fontWeight: 500 };

  const statusConfig = {
    success: { label: "Success", color: "#22c55e", bg: "#f0fdf4" },
    in_progress: { label: "In Progress", color: "#3b82f6", bg: "#eff6ff" },
    pending: { label: "Pending", color: "#f59e0b", bg: "#fffbeb" },
    failed: { label: "Failed", color: "#ef4444", bg: "#fef2f2" },
  };

  const maxCount = Math.max(...Object.values(statusCounts), 1);

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <div style={{ ...cardStyle }}>
          <span style={{ ...numberStyle, color: "#6366f1" }}>{total}</span>
          <span style={labelStyle}>📦 All Compounds</span>
        </div>
        <div style={{ ...cardStyle }}>
          <span style={{ ...numberStyle, color: "#8b5cf6" }}>{designCount}</span>
          <span style={labelStyle}>🎯 Design</span>
        </div>
        <div style={{ ...cardStyle }}>
          <span style={{ ...numberStyle, color: "#06b6d4" }}>{synthesisTotal}</span>
          <span style={labelStyle}>⚗️ Synthesis</span>
        </div>
        <div style={{ ...cardStyle }}>
          <span style={{ ...numberStyle, color: "#f59e0b" }}>{activityCount}</span>
          <span style={labelStyle}>🔬 Activity</span>
        </div>
      </div>

      {/* Synthesis breakdown */}
      {synthesisTotal > 0 && (
        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151", marginBottom: 8 }}>
            ⚗️ Synthesis Breakdown
          </div>
          {Object.entries(statusConfig).map(([key, cfg]) => {
            const count = statusCounts[key] || 0;
            if (count === 0) return null;
            const pct = synPct(count);
            return (
              <div key={key} style={{ marginBottom: 5 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ fontSize: "0.78rem", color: cfg.color, fontWeight: 500 }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: "0.78rem", color: "#64748b" }}>
                    {count} ({pct}%)
                  </span>
                </div>
                <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
                  <div style={{ background: cfg.color, width: `${pct}%`, height: 6, borderRadius: 4, transition: "width 0.3s" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default observer(CompoundsStats);
